// ─── The NuLife Timeline ─────────────────────────────────────────────────────
//
// "One component, two states. Not hard-coded to any single entity — a Timeline
// can attach wherever NuLife needs time input or time representation: Tasks,
// Events, People/Steward duties, Locations, setup schedules, Assets/bookings,
// appointments, and other time-bound objects."
//
// So nothing here knows what it is showing. It takes spans and labels; the
// caller supplies them from TimeBlocks. That is what lets Home's hero, a
// steward's duty bar and the Schedule page be the same component at different
// zoom levels rather than three things that resemble each other.

import { useState, type ReactNode } from "react";
import {
  type Span, fillFraction, blockGeometry, timeAt, timeLabel, buildDaySlots,
  buildDayDots, splitAcrossDays, type DaySlot, SNAP_MINUTES, snapTime,
} from "./timeline-math";
import { useDragFraction, HitArea, CloseButton } from "./primitives";

// ─── View state ──────────────────────────────────────────────────────────────

export type TimelineSegment = Span & { label: string; id?: string };

/**
 * The View-state bar: current activity filling proportionally, with what's
 * next labelled after it.
 *
 * "The same mental model as a TV channel's progress letterbox, so a glance
 * tells you how far through the current thing you are without reading a
 * clock."
 *
 * A reminder carried from timeline-math: the fill is a clock against a
 * schedule, not reported progress. Nothing on this bar should ever be read as
 * confirmation that work happened.
 */
export function TimelineBar({
  segments, now = new Date(), leadLabel, onOpen, compact,
}: {
  segments: TimelineSegment[];
  now?: Date;
  /** The zone/context this bar belongs to — "FIELD 1", a person's name. */
  leadLabel?: string;
  /** Single tap on any timeline opens it in a larger floating viewer. This is
   *  the general View-mode gesture; the caller wires it to <TimelineViewer>. */
  onOpen?: () => void;
  compact?: boolean;
}) {
  const current = segments.find(s => s.startsAt <= now && s.endsAt > now);
  const next = segments.find(s => s.startsAt > now);
  const fill = current ? fillFraction(current, now) : 0;

  return (
    <div className="flex items-center gap-3">
      {leadLabel && (
        <div
          className="shrink-0 text-sm uppercase tracking-wide"
          style={{ color: "var(--nl-ink-dim)", minWidth: compact ? undefined : "7.5rem" }}
        >
          {leadLabel}
        </div>
      )}
      <button
        type="button"
        onClick={onOpen}
        aria-label={
          current
            ? `${leadLabel ? leadLabel + ": " : ""}${current.label}, until ${timeLabel(current.endsAt)}`
            : `${leadLabel ? leadLabel + ": " : ""}nothing scheduled now`
        }
        className="relative flex-1 overflow-hidden text-left"
        style={{
          height: compact ? 22 : 28,
          borderRadius: "var(--nl-radius-sm)",
          background: "var(--nl-next)",
          border: "1px solid var(--nl-stroke)",
          cursor: onOpen ? "pointer" : "default",
        }}
      >
        {current ? (
          <>
            <div
              className="absolute inset-y-0 left-0"
              style={{ width: `${fill * 100}%`, background: "var(--nl-fill)" }}
            />
            <div className="relative flex h-full items-center justify-between px-2.5">
              <span
                className="truncate text-xs font-medium uppercase tracking-wide"
                style={{ color: fill > 0.12 ? "var(--nl-fill-ink)" : "var(--nl-ink)" }}
              >
                {current.label}
              </span>
              <span className="ml-2 shrink-0 text-xs" style={{ color: "var(--nl-ink)" }}>
                {timeLabel(current.endsAt)}
              </span>
            </div>
          </>
        ) : (
          <div className="flex h-full items-center px-2.5">
            <span className="text-xs" style={{ color: "var(--nl-ink-faint)" }}>
              {segments.length === 0 ? "Nothing scheduled" : "Between activities"}
            </span>
          </div>
        )}
      </button>
      {next && (
        <div className="hidden shrink-0 truncate text-xs uppercase sm:block" style={{ color: "var(--nl-ink-faint)", maxWidth: "9rem" }}>
          {next.label}
        </div>
      )}
    </div>
  );
}

/** A multi-segment bar — the steward profile's `FIELD 1 | BREAK | FIELD 2`
 *  strip, where the boundaries between segments carry the times. */
export function TimelineStrip({ window, segments, now = new Date() }: {
  window: Span; segments: TimelineSegment[]; now?: Date;
}) {
  return (
    <div
      data-nl-rail
      className="relative w-full overflow-hidden"
      style={{ height: 44, borderRadius: "var(--nl-radius-sm)", background: "var(--nl-track)", border: "1px solid var(--nl-stroke)" }}
    >
      {segments.map((s, i) => {
        const g = blockGeometry(window, s);
        const active = s.startsAt <= now && s.endsAt > now;
        return (
          <div
            key={s.id ?? i}
            className="absolute inset-y-0 flex items-center justify-center"
            style={{
              left: `${g.left}%`, width: `${g.width}%`,
              background: active ? "var(--nl-fill)" : "var(--nl-panel)",
              borderRight: "1px solid var(--nl-stroke)",
            }}
          >
            <span
              className="truncate px-2 text-xs uppercase tracking-wide"
              style={{ color: active ? "var(--nl-fill-ink)" : "var(--nl-ink-dim)" }}
            >
              {s.label}
            </span>
          </div>
        );
      })}
      {segments.slice(0, -1).map((s, i) => (
        <span
          key={`t${i}`}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 text-[10px]"
          style={{ left: `${blockGeometry(window, s).left + blockGeometry(window, s).width}%`, color: "var(--nl-ink)" }}
        >
          {timeLabel(s.endsAt)}
        </span>
      ))}
    </div>
  );
}

// ─── Compact date dots ───────────────────────────────────────────────────────

/**
 * "A small row of dots (~7 positions by default) represents **consecutive
 * calendar days**, not just days with activity — gaps stay visible as
 * inactive/grey dots rather than being skipped … Fade the last dot or two
 * toward the edge of the visible window as a subtle hint that the schedule
 * continues beyond it. Deliberately glanceable only."
 */
export function DayDots({ from, days = 7, spans, onOpen }: {
  from: Date; days?: number; spans: Span[]; onOpen?: () => void;
}) {
  const dots = buildDayDots(from, days, spans);
  const content = (
    <span className="inline-flex items-center gap-1.5">
      {dots.map((d, i) => {
        // The last two fade, hinting the schedule continues past the window.
        const fade = i >= dots.length - 2 ? (i === dots.length - 1 ? 0.35 : 0.65) : 1;
        return (
          <span
            key={d.date.toISOString()}
            className="inline-block rounded-full"
            style={{
              width: 10, height: 10, opacity: fade,
              background: d.active ? "var(--nl-status-on)" : "var(--nl-status-off)",
            }}
          />
        );
      })}
    </span>
  );
  return onOpen
    ? <HitArea label="Open schedule" onPress={onOpen}>{content}</HitArea>
    : <span aria-hidden>{content}</span>;
}

// ─── Day navigation ──────────────────────────────────────────────────────────

/**
 * "Days behave like adjacent pages — swipe horizontally to move between them …
 * with the active-day indicator moving as you swipe, or tap a day indicator to
 * jump directly to a date."
 *
 * Long ranges condense (see buildDaySlots), and a condensed run renders as an
 * explicit "+N days" rather than an anonymous dot, because the library says
 * date information has to stay legible.
 */
export function DayNavigator({ rangeStart, rangeEnd, active, onSelect, specialDays = {} }: {
  rangeStart: Date; rangeEnd: Date; active: Date;
  onSelect: (date: Date) => void;
  /** Presentation labels on dates — SHOW DAY, SETUP, PACKDOWN. Explicitly not
   *  a new object type, just a label. */
  specialDays?: Record<string, string>;
}) {
  const slots: DaySlot[] = buildDaySlots(rangeStart, rangeEnd, active);
  const key = (d: Date) => d.toISOString().slice(0, 10);
  return (
    <div className="nl-scroll-x flex items-stretch gap-1.5 py-1" role="tablist" aria-label="Day">
      {slots.map((slot, i) =>
        slot.kind === "gap" ? (
          <span
            key={`gap${i}`}
            className="flex items-center px-2 text-xs"
            style={{ color: "var(--nl-ink-faint)" }}
            aria-label={`${slot.count} more days`}
          >
            +{slot.count}
          </span>
        ) : (
          <button
            key={key(slot.date)}
            role="tab"
            aria-selected={key(slot.date) === key(active)}
            onClick={() => onSelect(slot.date)}
            className="flex min-w-[3.5rem] flex-col items-center gap-0.5 px-2 py-1.5"
            style={{
              borderRadius: "var(--nl-radius-sm)",
              background: key(slot.date) === key(active) ? "var(--nl-tile-hover)" : "transparent",
              border: `1px solid ${key(slot.date) === key(active) ? "var(--nl-stroke-strong)" : "transparent"}`,
              color: key(slot.date) === key(active) ? "var(--nl-ink)" : "var(--nl-ink-dim)",
              cursor: "pointer",
            }}
          >
            <span className="text-[10px] uppercase tracking-wide">{slot.label}</span>
            <span className="text-sm">{slot.dayOfMonth}</span>
            {specialDays[key(slot.date)] && (
              <span className="text-[9px] uppercase" style={{ color: "var(--nl-status-available)" }}>
                {specialDays[key(slot.date)]}
              </span>
            )}
          </button>
        )
      )}
    </div>
  );
}

// ─── Edit state ──────────────────────────────────────────────────────────────

export type EditableBlock = Span & { id: string; label?: string };

/**
 * Edit state: drag Start and End handles instead of clock selectors.
 *
 * Everything the library resolved on 2026-08-05 is here, and each piece exists
 * for a stated reason rather than as a flourish:
 *
 *  • Handles only become large touch targets once a block is SELECTED, so the
 *    rail doesn't become a minefield of grab zones.
 *  • 15-minute snapping, with an exact-time override for real precision.
 *  • Multiple blocks per Task/Event via "Add time" — three toilet checks in a
 *    day is one Task, not three.
 *  • **The list below is not decoration.** "Alongside the visual timeline, a
 *    plain list of each block's exact start/end with an Edit control per row.
 *    Dragging stays the fast primary interaction; the list is the precision/
 *    accessibility/reliability fallback, not a replacement." A drag interface
 *    with no typed fallback is unusable with gloves, in rain, or with a
 *    screen reader — all real Farndale conditions.
 */
export function TimelineEditor({
  window, blocks, onChange, snapMinutes = SNAP_MINUTES,
}: {
  window: Span;
  blocks: EditableBlock[];
  onChange: (blocks: EditableBlock[]) => void;
  snapMinutes?: number;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(blocks[0]?.id ?? null);

  const update = (id: string, patch: Partial<Span>) => {
    onChange(blocks.map(b => {
      if (b.id !== id) return b;
      const next = { ...b, ...patch };
      // A block may never invert; nudge by one increment instead of allowing
      // an end before its start.
      if (next.endsAt <= next.startsAt) {
        if (patch.startsAt) next.startsAt = new Date(next.endsAt.getTime() - snapMinutes * 60_000);
        else next.endsAt = new Date(next.startsAt.getTime() + snapMinutes * 60_000);
      }
      return next;
    }));
  };

  const addBlock = () => {
    const start = snapTime(new Date(window.startsAt.getTime() + 2 * 60 * 60_000), snapMinutes);
    onChange([...blocks, {
      id: `b${Date.now()}`,
      startsAt: start,
      endsAt: new Date(start.getTime() + 30 * 60_000),
    }]);
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        data-nl-rail
        className="relative w-full"
        style={{ height: 56, borderRadius: "var(--nl-radius-sm)", background: "var(--nl-track)", border: "1px solid var(--nl-stroke)" }}
      >
        {blocks.map(b => (
          <EditableBlockView
            key={b.id}
            window={window} block={b}
            selected={selectedId === b.id}
            onSelect={() => setSelectedId(b.id)}
            onMove={f => {
              const duration = b.endsAt.getTime() - b.startsAt.getTime();
              const start = timeAt(window, f, snapMinutes);
              update(b.id, { startsAt: start, endsAt: new Date(start.getTime() + duration) });
            }}
            onDragStart={f => update(b.id, { startsAt: timeAt(window, f, snapMinutes) })}
            onDragEnd={f => update(b.id, { endsAt: timeAt(window, f, snapMinutes) })}
          />
        ))}
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button" onClick={addBlock}
          className="px-3 py-1.5 text-sm"
          style={{ borderRadius: "var(--nl-radius-sm)", border: "1px solid var(--nl-stroke)", background: "var(--nl-tile)", color: "var(--nl-ink)", cursor: "pointer" }}
        >
          + Add time
        </button>
        <span className="text-xs" style={{ color: "var(--nl-ink-faint)" }}>
          Snapping to {snapMinutes} min
        </span>
      </div>

      {/* The precision / accessibility / reliability fallback. */}
      <ul className="flex flex-col gap-1.5" aria-label="Time blocks">
        {blocks.map(b => (
          <li
            key={b.id}
            className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
            style={{ borderRadius: "var(--nl-radius-sm)", background: "var(--nl-tile)", border: `1px solid ${selectedId === b.id ? "var(--nl-stroke-strong)" : "var(--nl-stroke)"}` }}
          >
            <ExactTimeField
              label="Start" value={b.startsAt}
              onChange={v => update(b.id, { startsAt: v })}
            />
            <span style={{ color: "var(--nl-ink-faint)" }}>→</span>
            <ExactTimeField
              label="End" value={b.endsAt}
              onChange={v => update(b.id, { endsAt: v })}
            />
            <button
              type="button"
              onClick={() => onChange(blocks.filter(x => x.id !== b.id))}
              className="ml-auto px-2 py-1 text-xs"
              style={{ color: "var(--nl-ink-dim)", background: "none", border: 0, cursor: "pointer" }}
              aria-label={`Delete block starting ${timeLabel(b.startsAt)}`}
            >
              Delete
            </button>
          </li>
        ))}
        {blocks.length === 0 && (
          <li className="px-3 py-2 text-sm" style={{ color: "var(--nl-ink-faint)" }}>
            No time set yet — drag on the timeline or use “Add time”.
          </li>
        )}
      </ul>
    </div>
  );
}

/** The exact-time override the library asks for alongside snapping. */
function ExactTimeField({ label, value, onChange }: {
  label: string; value: Date; onChange: (d: Date) => void;
}) {
  const hhmm = `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
  return (
    <label className="inline-flex items-center gap-1.5">
      <span className="text-xs" style={{ color: "var(--nl-ink-faint)" }}>{label}</span>
      <input
        type="time"
        value={hhmm}
        onChange={e => {
          const [h, m] = e.target.value.split(":").map(Number);
          // `undefined` as well as NaN, and not merely to satisfy the
          // compiler: a cleared <input type="time"> reports "", which splits
          // to one element, so `m` is genuinely absent and
          // `Number.isNaN(undefined)` is false. Farndale's copy lets that
          // through to `setHours(h, undefined)`. A correctness fix, not a
          // design one — worth carrying back to Farndale at Stage 2.
          if (h === undefined || m === undefined) return;
          if (Number.isNaN(h) || Number.isNaN(m)) return;
          const next = new Date(value);
          next.setHours(h, m, 0, 0);
          onChange(next);
        }}
        className="px-1.5 py-0.5 text-sm"
        style={{ background: "var(--nl-bg)", color: "var(--nl-ink)", border: "1px solid var(--nl-stroke)", borderRadius: 6 }}
      />
    </label>
  );
}

function EditableBlockView({
  window, block, selected, onSelect, onMove, onDragStart, onDragEnd,
}: {
  window: Span; block: EditableBlock; selected: boolean;
  onSelect: () => void;
  onMove: (f: number) => void;
  onDragStart: (f: number) => void;
  onDragEnd: (f: number) => void;
}) {
  const g = blockGeometry(window, block);
  const moveDrag = useDragFraction(onMove);
  const startDrag = useDragFraction(onDragStart);
  const endDrag = useDragFraction(onDragEnd);

  return (
    <div
      className="absolute inset-y-1.5"
      style={{ left: `${g.left}%`, width: `${g.width}%` }}
    >
      <div
        role="button" tabIndex={0}
        aria-label={`Block ${timeLabel(block.startsAt)} to ${timeLabel(block.endsAt)}${selected ? ", selected" : ""}`}
        onClick={onSelect}
        onKeyDown={e => { if (e.key === "Enter") onSelect(); }}
        {...moveDrag}
        className="relative flex h-full items-center justify-center overflow-hidden touch-none"
        style={{
          borderRadius: 8,
          background: "var(--nl-fill)",
          border: `2px solid ${selected ? "var(--nl-ink)" : "transparent"}`,
          cursor: "grab",
        }}
      >
        <span className="truncate px-1 text-[11px]" style={{ color: "var(--nl-fill-ink)" }}>
          {timeLabel(block.startsAt)}–{timeLabel(block.endsAt)}
        </span>
      </div>

      {/* Handles are only large touch targets once the block is selected —
       *  otherwise the rail becomes wall-to-wall grab zones. */}
      {selected && (
        <>
          <Handle side="left" {...startDrag} />
          <Handle side="right" {...endDrag} />
        </>
      )}
    </div>
  );
}

function Handle({ side, onPointerDown }: {
  side: "left" | "right";
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
}) {
  return (
    <span
      role="slider"
      aria-label={side === "left" ? "Start time" : "End time"}
      aria-valuetext={side}
      tabIndex={0}
      onPointerDown={onPointerDown}
      className="absolute top-1/2 -translate-y-1/2 touch-none"
      style={{
        [side]: -10,
        // Small visible mark, generous invisible hit area — the field-use rule.
        width: "var(--nl-hit-min)", height: "var(--nl-hit-min)",
        display: "grid", placeItems: "center", cursor: "ew-resize",
      } as React.CSSProperties}
    >
      <span
        className="block"
        style={{ width: 8, height: 26, borderRadius: 4, background: "var(--nl-ink)", boxShadow: "var(--nl-shadow)" }}
      />
    </span>
  );
}

// ─── Floating viewer ─────────────────────────────────────────────────────────

/**
 * "Single tap on any timeline, anywhere it appears, opens that individual
 * timeline in a larger floating viewer — easy reading, scrolling, day
 * navigation. Tap out/close to return to where you were."
 *
 * The general View-mode gesture. Home's double-tap-to-expand is scoped to Live
 * Status specifically and is deliberately NOT implemented here as a global
 * rule — the library says to promote it later if it proves useful elsewhere.
 */
export function TimelineViewer({
  title, window, segments, rangeStart, rangeEnd, activeDay, onSelectDay, onClose, children,
}: {
  title: string;
  window: Span;
  segments: TimelineSegment[];
  rangeStart: Date; rangeEnd: Date; activeDay: Date;
  /** Optional: a viewer showing one fixed day has no day to switch to, and a
   *  navigator that cannot navigate is a control that does nothing. */
  onSelectDay?: (d: Date) => void;
  onClose: () => void;
  children?: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      style={{ background: "var(--nl-scrim)" }}
      onClick={onClose}
      role="dialog" aria-modal="true" aria-label={`${title} timeline`}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-2xl p-5"
        style={{
          background: "var(--nl-bg-raised)", color: "var(--nl-ink)",
          borderRadius: "var(--nl-radius-lg) var(--nl-radius-lg) 0 0",
          border: "1px solid var(--nl-stroke)", boxShadow: "var(--nl-shadow)",
        }}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg">{title}</h2>
          <CloseButton onPress={onClose} />
        </div>
        {onSelectDay && (
          <DayNavigator rangeStart={rangeStart} rangeEnd={rangeEnd} active={activeDay} onSelect={onSelectDay} />
        )}
        <div className="mt-3">
          <TimelineStrip window={window} segments={segments} />
        </div>
        {/* A cross-midnight block is drawn on each day it touches while
         *  remaining one stored block — see splitAcrossDays. */}
        {segments.some(s => splitAcrossDays(s).length > 1) && (
          <p className="mt-2 text-xs" style={{ color: "var(--nl-ink-faint)" }}>
            Continues past midnight.
          </p>
        )}
        {children}
      </div>
    </div>
  );
}
